import { createSelector, weakMapMemoize } from 'reselect'
import moment from 'moment'
import { buildSearchParamsKey } from 'lib/search/searchUtils'
import { arrayToObject, sortBy } from 'lib/array/arrayUtils'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { getBedbankRateKey } from 'lib/hotels/getBedbankRateKey'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { UTM_CONFIGS } from 'constants/utms'
import { SOURCE_TYPES } from 'constants/source'
import { isLpcHotelOffer } from 'lib/offer/offerTypes'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { isGHARedirection } from 'lib/optimizely/optimizelyUtils'
import { isPropertyBlacklisted } from 'lib/metasearch/metasearchBlacklist'
import { isEligibleMetasearchCugRateRule, isLiveMetasearchRateRule } from 'lib/metasearch/metasearchRateRules'

const formatSourceTypes = (sourceTypes: Array<string>): string =>
  Array.from(new Set(sourceTypes.filter(Boolean))).join(',')

const replaceMetasearchRateSource = (source: string, metasearchSource: string): string =>
  formatSourceTypes([
    ...source.split(',').filter(sourceType =>
      sourceType &&
      sourceType !== SOURCE_TYPES.METASEARCH &&
      sourceType !== SOURCE_TYPES.CUG_METASEARCH,
    ),
    metasearchSource,
  ])

export function getTourV2SelectedPackageType(state: App.State, offerId: string): string | undefined {
  if (state.offer.tourV2SelectedPackageType === undefined) {
    return undefined
  }
  return state.offer.tourV2SelectedPackageType[offerId]
}

export function getSuggestedDates(state: App.State, filterKey: string, offerId: string): App.OfferSuggestedDates | undefined {
  return state.offer.searchResultMetadata.offerSuggestedDates?.[filterKey]?.[offerId]
}

interface GetOffersBestPricesParams {
  offers: Array<App.HotelOffer | App.BedbankOffer | App.HotelOfferSummary | App.BedbankOfferSummary | App.VillaOffer | App.VillaOfferSummary | App.TourOffer | App.TourOfferSummary>
  filters: App.OfferListFilters
}

export interface OfferBestPriceData {
  bestPrice: number
  bestMemberPrice: number
  bestBaseMemberPrice: number
  loading: boolean
  soldOut: boolean
  duration?: number
  bestPricePackage?: App.Package
  error?: any
}

/**
 * Returns "best price" information for the given offers.
 * Price given is the total for all rooms, NOT per room.
 */
export const getOffersBestPrices = createSelector(
  (state: App.State) => state.offer.offerBestPrices,
  (state: App.State) => state.offer.offerPricesLoading,
  (state: App.State) => state.offer.offerPricesErrors,
  (state: App.State) => state.offer.bedbankOfferRates,
  (state: App.State) => state.offer.offerRatesLoading,
  (state: App.State, params: GetOffersBestPricesParams) => params.offers,
  (state: App.State, params: GetOffersBestPricesParams) => params.filters,
  function combiner(bestPrices, pricesLoading, pricesErrors, bedbankOfferRates, bedbankOfferRatesLoading, offers, filters): Record<string, OfferBestPriceData> {
    const searchKey = buildSearchParamsKey(filters.checkIn, filters.checkOut, filters.rooms)
    const hasDates = !!(filters.checkIn && filters.checkOut)
    return arrayToObject(
      offers,
      (offer) => offer.id,
      (offer): OfferBestPriceData => {
        let duration = hasDates ? moment(filters.checkOut).diff(moment(filters.checkIn), 'days') : undefined

        if (offer.type === OFFER_TYPE_BED_BANK) {
          const rateKey = filters.rooms && filters.checkIn && filters.checkOut ? getBedbankRateKey(filters.rooms, filters.checkIn, filters.checkOut) : ''
          const lowestRate = bedbankOfferRates[offer.id]?.[rateKey]?.[0]
          const loading = bedbankOfferRatesLoading[offer.id] || hasDates && !lowestRate

          const soldOut = hasDates && !lowestRate && !loading
          let bestPrice: number = 0
          if (lowestRate) {
            bestPrice = lowestRate.totals.total
            duration = lowestRate.nights
          }

          return {
            bestPrice,
            bestMemberPrice: 0,
            bestBaseMemberPrice: 0,
            duration,
            loading,
            soldOut,
          }
        } else {
          const bestPriceInfo = bestPrices[offer.id]?.[searchKey]
          const available = bestPriceInfo?.available
          const rate = available ? bestPriceInfo.rate : undefined
          const error = pricesErrors[offer.id]?.[searchKey]
          let loading = pricesLoading[offer.id]?.[searchKey]
          if (hasDates && !bestPriceInfo && typeof loading === 'undefined') {
            // If we don't have a rate and haven't even started loading it yet, for all intents and purposes it is "loading"
            loading = true
          }

          const soldOut = hasDates && bestPriceInfo && !available

          let bestPricePackage: App.HotelPackage | App.TourPackage | undefined
          if (available && rate) {
            bestPricePackage = offer.packages.find(pkg => pkg.uniqueKey === rate.packageUniqueKey)
          } else if (!hasDates) {
            let bpPackage

            if (isLpcHotelOffer(offer) && offer.hasTactical) {
              const allPackagesFilteredByLowestPrice = (offer.packages ?? []).filter(pkg => pkg.duration === offer.lowestPricePackage?.duration)
              const packagedViews = allPackagesFilteredByLowestPrice.filter(pkg => pkg.roomRate?.isPackaged)

              for (const view of packagedViews) {
                if (view.price > 0) {
                  const indexPackageViewByDuration = allPackagesFilteredByLowestPrice.findIndex(x => x.roomType?.id === view.roomType?.id && x.roomRate?.packagedRatePlanId === view.roomRate?.ratePlanId)
                  if (indexPackageViewByDuration >= 0) {
                    allPackagesFilteredByLowestPrice[indexPackageViewByDuration] = view
                  } else {
                    allPackagesFilteredByLowestPrice.push(view)
                  }
                }
              }

              bpPackage = sortBy(allPackagesFilteredByLowestPrice.filter(pkg => pkg.hasTactical && pkg.price > 0), p => p.price, 'asc')[0]
            }

            bestPricePackage = bpPackage || offer.lowestPricePackage
          }

          let bestPrice: number = 0
          let bestMemberPrice = 0
          let bestBaseMemberPrice = 0
          if (bestPricePackage) {
            const roomCount = filters?.rooms?.length || 1
            const extraGuestSurcharges = getSumExtraGuestsSurcharge(filters.rooms ?? [], offer, bestPricePackage)
            bestPrice = (rate?.price ?? bestPricePackage.price) * roomCount + extraGuestSurcharges
            bestBaseMemberPrice = rate?.memberPriceWithSurcharge ?? bestPricePackage.memberPrice
            bestMemberPrice = bestBaseMemberPrice * roomCount + extraGuestSurcharges
            duration = bestPricePackage.duration
          }

          return {
            bestPrice,
            bestMemberPrice,
            bestBaseMemberPrice,
            duration,
            loading,
            error,
            soldOut,
            bestPricePackage,
          }
        }
      },
    )
  },
)

/**
 * Use to select for a combined state of all offers by id across the app - does not include summary offers
 *
 * In theory, one day, this should be a single field in a reducer but for now
 * it's spread across many fields based on offer type.
 */
export const getFullOffers = createSelector(
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.cruise.cruiseOffers,
  (state: App.State) => state.offer.tourV2Offers,
  (offers, bedbankOffers, cruises, tours) => {
    return {
      ...cruises,
      ...bedbankOffers,
      ...offers,
      ...tours,
    }
  },
)

/**
 * Use to select for a combined state of all offers by id across the app
 *
 * In theory, one day, this should be a single field in a reducer but for now
 * it's spread across many fields based on offer type.
 */
export const getAllOffers = createSelector(
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.offerSummaries,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.offer.bedbankOfferSummaries,
  (state: App.State) => state.cruise.cruiseOffers,
  (state: App.State) => state.offer.tourV2Offers,
  (state: App.State) => state.experience.experiences,
  (offers, offerSummaries, bedbankOffers, bedbankSummaries, cruises, tours, experiences): Record<string, App.AnyOffer> => {
    return {
      ...cruises,
      ...tours,
      ...offerSummaries,
      ...bedbankSummaries,
      ...bedbankOffers,
      ...offers,
      ...experiences,
    }
  },
)

export const getAllAirportTransferOffers = createSelector(
  (state: App.State) => state.experience.experiences,
  (experiences): Record<string, App.ExperienceOffer> => {
    return {
      ...experiences,
    }
  },
)

export const isPaidSession = createSelector(
  (state: App.State) => state.utm,
  (utm_params): boolean => {
    const utmStateArray = Object.keys(utm_params)
    return utmStateArray.some(utm => UTM_CONFIGS.clickIdentifiers.utms.includes(utm))
  },
)

// Page-level source. For per-property source (e.g. rate fetching), use getSourceForProperty.
// Do NOT remove the blacklist check in the bp=3 block below — many partner page components
// use this directly and rely on it returning B2C for blacklisted properties.
export const getSource = createSelector(
  (state: App.State) => state.router.location.query?.source ?? undefined,
  (state: App.State) => state.router.location.query?.priceCheck ?? undefined,
  (state: App.State) => state.router.location.pathname,
  (state: App.State) => state.utm,
  (state: App.State) => state.auth,
  (state: App.State) => state.marketing.metaOffersDiscount,
  (state: App.State) => state.marketing.metasearchPropertyBlacklist,
  (state: App.State) => state.system.rawUserAgentString,
  (source, priceCheck, pathname, utm, auth, metaOfferDiscount, blacklist, userAgent): string => {
    // TEMPORARY FIX
    // Temporary workaround for the GHA price scraper until we have a better solution for the Agoda rate issue.
    // When a GHA metasearch (bp=3) request comes from Google's price scraper (identified by user agent
    // or the priceCheck=true param), append metasearchPriceVerifier to the end of the normal source instead
    // of replacing it, so the underlying pricing source is preserved. This only affects the GHA scraper,
    // not real customer traffic.
    const isGHAPriceScraper = utm.bp === '3' && (userAgent.includes('Google-HotelAdsVerifier') || priceCheck === 'true')
    // Live pricing serves the full multi-rate Agoda result, so the verifier's single rate
    // understates the price and trips GHA price-accuracy checks — skip it for live (GR-4960).
    const isLivePriceCheck = isLiveMetasearchRateRule(utm.ruleId)
    const withValidation = (result: string): string =>
      isGHAPriceScraper && !isLivePriceCheck ? formatSourceTypes([result, SOURCE_TYPES.METASEARCH_PRICE_VERIFIER]) : result

    let bedbankOfferId = ''
    if (pathname.includes('/partner/')) {
      bedbankOfferId = pathname.split('/').pop() ?? ''
    }
    const isMatchedMetaOfferDiscount = Object.keys(metaOfferDiscount).length > 0 && Object.keys(metaOfferDiscount)[0] === bedbankOfferId
    const sourceData: Array<string> = []
    if (pathname.includes('/partner/')) {
      sourceData.push(SOURCE_TYPES.OFFER)
    } else if (pathname.includes('/offer/')) {
      sourceData.push(SOURCE_TYPES.OFFER)
    } else if (pathname.includes('/search')) {
      sourceData.push(SOURCE_TYPES.SEARCH)
    } else if (pathname.includes('/checkout/')) {
      sourceData.push(SOURCE_TYPES.CHECKOUT)
    }

    const memberId = auth.account?.memberId
    const bp = utm.bp

    if (isMatchedMetaOfferDiscount) {
      return withValidation(bp === '3' ? replaceMetasearchRateSource(SOURCE_TYPES.OFFER, SOURCE_TYPES.CUG_METASEARCH) : SOURCE_TYPES.OFFER)
    }

    // When bp=2 and user is not logged in, send source=metasearch to get B2C rates from svc-public-offer & svc-bedbank
    if (!memberId && bp === '2' && source !== SOURCE_TYPES.METASEARCH) {
      sourceData.push(SOURCE_TYPES.METASEARCH)
    } else if (memberId && bp === '2') {
      sourceData.push(SOURCE_TYPES.LOGGEDIN_METASEARCH)
    }

    // GHA metasearch (bp=3): default to B2C rates (metasearch source) for all offers.
    // This is the baseline behaviour — metasearch source forces B2C rates from svc-bedbank.
    if (bp === '3') {
      sourceData.push(SOURCE_TYPES.METASEARCH)
    }

    if (source) {
      // Don't push metasearch if user is logged in, or if source is already present
      const shouldInsertSource = !(memberId && source === SOURCE_TYPES.METASEARCH) && !sourceData.includes(source)

      if (shouldInsertSource) {
        sourceData.push(source)
      }
    }

    const pageSource = formatSourceTypes(sourceData)

    if (bp === '3' && isEligibleMetasearchCugRateRule(utm.ruleId)) {
      return withValidation(replaceMetasearchRateSource(pageSource, SOURCE_TYPES.CUG_METASEARCH))
    }

    // GHA CUG metasearch (bp=3): request CUG via cugMetasearch, except for
    // blacklisted properties on /partner/ pages which must keep B2C metasearch as the safe default.
    if (bp === '3') {
      const isBlacklisted = bedbankOfferId && isPropertyBlacklisted(blacklist, bedbankOfferId)
      if (!isBlacklisted) {
        return withValidation(replaceMetasearchRateSource(pageSource, SOURCE_TYPES.CUG_METASEARCH))
      }
      return withValidation(replaceMetasearchRateSource(pageSource, SOURCE_TYPES.METASEARCH))
    }

    return withValidation(pageSource)
  },
)

// Per-property source override. Keeps metasearch for blacklisted properties (B2C),
// and uses cugMetasearch for non-blacklisted properties (CUG for YMAL).
// All individual property rate-fetching code should use this instead of getSource if possible.
export const getSourceForProperty = createSelector(
  (state: App.State, _propertyId: string) => getSource(state),
  (state: App.State) => state.utm.bp,
  (state: App.State) => state.utm.ruleId,
  (state: App.State) => state.marketing.metasearchPropertyBlacklist,
  (_state: App.State, propertyId: string) => propertyId,
  (baseSource, bp, ruleId, blacklist, propertyId): string => {
    if (bp !== '3') return baseSource
    // Eligible GHA CUG rules must stay CUG even when the property is blacklisted.
    // The blacklist fallback below only applies to non-CUG GHA rates that need safe B2C pricing.
    if (isEligibleMetasearchCugRateRule(ruleId)) return replaceMetasearchRateSource(baseSource, SOURCE_TYPES.CUG_METASEARCH)

    if (isPropertyBlacklisted(blacklist, propertyId)) {
      return replaceMetasearchRateSource(baseSource, SOURCE_TYPES.METASEARCH)
    }

    return replaceMetasearchRateSource(baseSource, SOURCE_TYPES.CUG_METASEARCH)
  },
  { memoize: weakMapMemoize, argsMemoize: weakMapMemoize },
)

// Temporarily disable pay-at-property (property collect) rates for GHA metasearch traffic.
// The metasearch pipeline does not yet pass enablePropertyCollect, causing rate mismatches
// between the metasearch page and the offer page (partner page).
export function isPropertyCollectEnabled(state: App.State): boolean {
  const flagEnabled = !!state.system.features.flags[OptimizelyFeatureFlags.croPayAtProperty]
  if (!flagEnabled) return false
  return !isGHARedirection(state.utm.content)
}
